import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
   Box,
   Divider,
   Grid,
   useMediaQuery,
   useTheme,
} from "@mui/material";
import { Typography } from "@mui/material";
import React from "react";
import { Accelerator } from "../../interfaces/accelerator";
import { Lesson } from "../../interfaces/lesson";
import { axiosRequest } from "../../helpers/axios";
import CoursePlayer from "../../components/coursePlayer/coursePlayer";
import { Course, CourseType } from "../../interfaces/course";
import { getLastPartOfUrl, getTimeFromSeconds } from "../../helpers/utils";
import { Params, useParams } from "react-router-dom";
import PageLoader from "../../components/loader/loader";
import { trainingProgramViewed } from "../../components/mixPanelEvents/mixPanelEvents";
import { Link } from "../../interfaces/link";
import Carousel from "../../components/carouselCourse/carousel";
import SliderCard from "../../components/cards/sliderCard/sliderCard";
import { useNavigate } from "react-router";
import TrainingCardCarousel from "../../components/cards/trainingCards/trainingCardCarousel";
import useHighlightCurrentMenuItem from "../../hooks/useHighlightCurrentMenuItem";
import useSurveyAnswered from "../../hooks/useSurveyAnswered";
import BreadcrumbsNav from "../../components/navigation/breadcrumbs-nav/BreadcrumbsNav";
import type { BreadcrumbItem } from "../../components/navigation/types/BreadcrumbItem";

declare global {
   interface Window {
      courseId: number;
      playlistCourseId?: number;
      playlistCourseType?: string;
   }
}

interface CurrentLesson extends Lesson {
   courseID: number;
   ordinal: number;
}

const CoursePage = () => {

   useHighlightCurrentMenuItem('/');
   const {
      isSurveyAnswered: guidedJourneyIsAnswered,
      loading: guidedJourneyLoading
   } = useSurveyAnswered('guided_journey');

   const params = useParams();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
   const [containerWidth, setContainerWidth] = useState(0);
   const containerRef = useRef<HTMLDivElement>(null);
   const updateContainerWidth = () => {
      if (containerRef.current) {
         setContainerWidth(containerRef.current.offsetWidth);
      }
   };
   const [course, setCourse] = useState<Course>({
      ID: 0,
      image: "",
      lessons: [],
      name: "",
      progress: 0,
      url: "",
      module: "",
      slider: [],
      allow_text_lesson_as_default: false,
      cover_video: "",
      hasCpBanner: false,
      course_type: CourseType.Default,
      collapsable_accordions: false
   });
   const [accelerator, setAccelerator] = useState<Accelerator>({
      ID: 0,
      courses: [],
      image: "",
      name: "",
      progress: 0,
      coach: 0,
      quickfix: [],
      url: "",
      first_time: false,
      isCourse: true,
      promo: [],
      hasCpBanner: false,
      showVipBannerToAllLessons: false,
      ctaUrlForAllLessons: "",
      ctaTextForAllLessons: "",
      course_type: CourseType.Default,
      is_autoplay: false,
      collapsable_accordions: false
   });
   const [currentLesson, setCurrentLesson] = useState<CurrentLesson>({
      content: "",
      lesson_type: "",
      url: "",
      drill_redirect_url: "",
      records: [],
      ID: 0,
      complete: false,
      courseID: 0,
      duration: "",
      name: "",
      is_pro_edition: false,
      ordinal: 0,
      is_fav: false,
      link: {} as Link,
      video: {
         ID: 0,
         progress: 0,
         url: "",
         embed_url: "",
         duration: "",
         subheading: "",
         button_text: "",
         button_url: "",
         text_under_button: ""
      },
      drill: {
         game_rules: "",
         record_label: "",
         record_label_desc: "",
         scoring_system: "",
         subheading: "",
      },
      cta: {
         text: "",
         url: "",
      },
      linkIcon: ""
   });
   const [loaded, setLoaded] = useState(false);
   const navigate = useNavigate();
   const userFlags = useMemo(() => window.pgUser?.flags || [], [window.pgUser]);
   const hasFetchedRef = useRef(false);

   const [popularCoursesOnly, setPopularCoursesOnly] = useState<any>(null);

   function getLessonSlug(url: string): string {
      const pathSegments = new URL(url).pathname.split('/').filter(segment => segment !== '');
      const lessonSlug = pathSegments[2] || '';
      return lessonSlug == "" ? pathSegments[1] : lessonSlug;
   }

   useEffect(() => {
      const observer = new ResizeObserver((entries) => {
         for (let entry of entries) {
            updateContainerWidth(); // Call the function to update container width
         }
      });

      if (containerRef.current) {
         observer.observe(containerRef.current);
         updateContainerWidth(); // Call the function to update container width initially
      }

      return () => {
         observer.disconnect();
      };
   }, [containerRef.current]);
   useEffect(() => {
      // Prevent infinite loops by checking if already fetched
      if (hasFetchedRef.current) {
         return;
      }

      hasFetchedRef.current = true;

      // Check if we're on a playlist course URL and need to find the correct course ID
      const getCorrectCourseId = async (): Promise<number> => {
         const currentPath = window.location.pathname;
         const pathSegments = currentPath.split('/').filter(Boolean);

         // Check if we have playlist course data from server
         if (window.playlistCourseId && window.playlistCourseType === 'playlist') {
            return window.playlistCourseId;
         }

         // Fallback to window.courseId
         return window.courseId || 65201;
      };


      const fetchCourseData = async (attempts = 3): Promise<void> => {
         try {
            const courseId = await getCorrectCourseId();

            if (courseId) {
               const res = await axiosRequest("GET", `/wp-json/pg/v2/course/${courseId}`);

               const responseC = res.data.data as Course;
               const response = res.data.data as Accelerator;

               if (response.showVipBannerToAllLessons) {
                  response.courses.forEach(course => {
                     course.lessons.forEach(lesson => {
                        if (lesson.lesson_type != "text") {
                           var slugForUtmCampaign = getLessonSlug(lesson.url);
                           const url = new URL(response.ctaUrlForAllLessons);
                           url.searchParams.set('utm_campaign', slugForUtmCampaign);
                           lesson.cta = { text: (lesson.cta ? lesson.cta.text : ""), url: url.toString() }
                        }
                     });
                  })
               }
               setCourse(responseC);
               setAccelerator(response);
               setLoaded(true);
            } else {
               window.open("/404", "_self");
            }
         } catch (error: any) {
            const responseData = error.data?.data;
            // If it's a 404, go to courses page:
            if (responseData?.status === 404) {
               window.open("/courses", "_self");
               return;
            }
            if (responseData?.status === 403 && responseData?.redirect_url) {
               window.open(responseData.redirect_url, "_self");
               return;
            }

            // Otherwise, do as many retries as defined:
            if (attempts > 1) {
               console.log("reties :", attempts);
               return fetchCourseData(attempts - 1);
            }

            // No more retries—redirect to courses
            window.open("/courses", "_self");
         }
      };



      fetchCourseData();
   }, [guidedJourneyIsAnswered]);

   useEffect(() => {
      if (loaded) {
         findAndSetFirstLesson(accelerator, params);
      }
   }, [loaded, accelerator, params]);




   const findAndSetFirstLesson = (
      coursesList: Accelerator,
      params: Readonly<Params<string>>
   ) => {
      let firstLesson: CurrentLesson = {} as CurrentLesson;
      if (params.lessonName) {
         const allLessons: CurrentLesson[] = coursesList.courses.reduce(
            (acc: CurrentLesson[], next: Course) => {
               return [
                  ...acc,
                  ...next.lessons.map((el, index) => {
                     return { ...el, courseID: next.ID, ordinal: index };
                  }),
               ];
            },
            []
         );
         const findLesson = allLessons.find((lesson) => {
            return getLastPartOfUrl(lesson.url) === params.lessonName;
         });
         if (findLesson) {
            firstLesson = findLesson;
         } else {
            window.open("/", "_self");
         }
      } else if (params.courseName) {
         const allLessons: CurrentLesson[] = coursesList.courses.reduce(
            (acc: CurrentLesson[], next: Course) => {
               return [
                  ...acc,
                  ...next.lessons
                     .filter((lesson) => lesson.lesson_type === "video" || (lesson.lesson_type === "text" && course.allow_text_lesson_as_default))
                     .map((el, index) => {
                        return { ...el, courseID: next.ID, ordinal: index };
                     }),
               ];
            },
            []
         );

         const firstVideoLesson = allLessons[0];
         if (firstVideoLesson) {
            firstLesson = {
               ...firstVideoLesson,
               courseID: firstVideoLesson.courseID,
               ordinal: 0,
            };
         }
      } else {
         window.open("/", "_self");
      }

      if (firstLesson.lesson_type === "video" || (firstLesson.lesson_type === "text" && course.allow_text_lesson_as_default)) {
         setCurrentLesson({
            ...firstLesson,
            duration: getTimeFromSeconds(Number(firstLesson.video.duration)),
         });
      } else {
         setCurrentLesson(firstLesson);
      }
   };
   // console.log(course);
   useEffect(() => {
      let vip =
         currentLesson.lesson_type === "cta" || currentLesson.cta
            ? "Yes"
            : "No";
      let type =
         trainingProgramViewed(currentLesson, accelerator, vip, currentLesson.lesson_type === "text" ? "RSVP VIP Golf School" : undefined);
      // eslint-disable-next-line
   }, [currentLesson]);

   const handleClick = (): void => {
      window.location.href = window.location.origin;
   };
   const breadcrumbItems: readonly BreadcrumbItem[] = [
      { label: accelerator.name },
   ];

   useEffect(() => {
      const fetchCourses = async () => {
         const response = await axiosRequest(
            'GET',
            '/wp-json/pg/v2/courses?popularOnly=true'
         );
         setPopularCoursesOnly(response.data.data.sections[0]);
      };

      fetchCourses();

      const handleResize = () => {
         const width = window.innerWidth;
         const x = interpolateValue(width);
         if (containerRef.current) {
            containerRef.current.style.width = `calc(100% + ${x}px)`;
         } else {
            setTimeout(() => {
               if (containerRef.current) {
                  containerRef.current.style.width = `calc(100% + ${x}px)`;
               } else {
                  setTimeout(() => {
                     if (containerRef.current) {
                        containerRef.current.style.width = `calc(100% + ${x}px)`;
                     }
                  }, 500);
               }
            }, 100);
         }
      };

      handleResize(); // Initial call to set the width

      window.addEventListener('resize', handleResize);

      return () => {
         window.removeEventListener('resize', handleResize);
      };
   }, []);

   const interpolateValue = (width: number) => {
      if (width >= 979 || width < 900) {
         return 0;
      } else if (width == 900) {
         return 70;
      } else {
         // Linearna interpolacija između 979 i 900
         return Math.floor(((979 - width) / (979 - 900)) * 60 + 10);
      }
   };

   return (
      <Fragment>
         <Box sx={ !isMobile ? { maxWidth: "1440px", margin: "0 auto" } : { } }>
         {!loaded && <PageLoader />}
         {loaded && (
            <Fragment>
               <Box
                  sx={{
                     padding: { xs: "0 16px 0px 16px", md: "0 60px 0px 60px" },
                     position: "relative",
                  }}
               >
                  <Box sx={{ marginTop: "20px", marginBottom: "-10px" }}>
                    <BreadcrumbsNav items={breadcrumbItems} separator="/" prefix="/" showIcon={true} />
                  </Box>
                  {currentLesson.lesson_type !== "text" && (
                     <Box my={{ xs: 2, md: 4 }}>
                        <Typography
                           variant="h1"
                           fontSize={{ xs: "36px !important", md: "78px !important" }}
                           fontWeight={ 700 }
                           textTransform={"uppercase"}
                           fontFamily={"Repro"}
                           lineHeight={0.87}
                           letterSpacing={"-0.5px"}
                           color="#18181B"
                        >
                           {accelerator.name}
                        </Typography>
                     </Box>
                  )}

                  {/* {currentLesson.lesson_type === "text" && isMobile ? null : <Box my={4}>
                     <Typography
                        variant="h1"
                        fontSize={{ xs: "28px", md: "30px" }}
                        fontWeight={{ xs: 600, md: 800 }}
                     >
                        {accelerator.name}
                     </Typography>
                  </Box>} */}

                  {accelerator.ID && !guidedJourneyLoading && (
                     <CoursePlayer
                        accelerator={accelerator}
                        currentLesson={currentLesson}
                        showGuidedJourney={
                           accelerator.ID === 65201
                           && (!guidedJourneyIsAnswered && !userFlags.includes('skip_guided_journey'))
                        } // Guided Journey is only enabled for SSTS at the moment
                        popularCoursesOnly={popularCoursesOnly}
                     ></CoursePlayer>
                  )}

                  {(currentLesson.lesson_type !== "text" || (currentLesson.lesson_type === "text" && !isMobile)) &&
                     <Divider
                        component="li"
                        sx={{ margin: 0, display: { xs: "block", md: "none" } }}
                     />
                  }
                  {isMobile && (
                     <Grid sx={{ padding: currentLesson.lesson_type == 'text' && isMobile ? "0 20px" : 0 }}>
                        {
                           <Grid sx={{ marginY: { xs: 3, md: 5 } }}>
                              <Grid container spacing={3}>
                                 <Grid item xs={12} md>
                                    <Box
                                       ref={containerRef}
                                       style={{
                                          width: "100%",
                                          background: "#fff",
                                          paddingTop: "0px",
                                          overflow: "hidden",
                                          marginLeft: 0,
                                       }}
                                       sx={{
                                          height: "auto",
                                          paddingLeft: "0 !important"
                                       }}
                                       className={"mainCoursesContainer"}
                                    >
                                       <div
                                          ref={containerRef}
                                          key={1}
                                          className="carouselAndTitleAndArrowsContainer"
                                          style={{ width: '100%' }}
                                       >
                                          {popularCoursesOnly?.things?.length > 0 && (
                                             <div className="coursesTrainingCarousel" style={{ fontFamily: "Repro" }}>
                                                <TrainingCardCarousel
                                                   items={popularCoursesOnly.things}
                                                   carouselTitle={popularCoursesOnly.name}
                                                   width={containerWidth}
                                                   parentPage={"course-player"}
                                                   isSidebarOpen={false}
                                                   locationValue="Footer"
                                                />
                                             </div>
                                          )}
                                       </div>
                                    </Box>
                                 </Grid>
                              </Grid>
                           </Grid>
                        }
                     </Grid>
                  )}
               </Box>
            </Fragment>
         )}
         </Box>
      </Fragment>
   );
};
export default CoursePage;
